import { Helmet, HelmetData } from "react-helmet-async";
import {
  AI_BG_NEW,
  Auto_Icon,
  Control_Icon,
  Convo_Query,
  Custom_Alert_Icon,
  DA_Fetaures_Img,
  DA_NEW_HERO,
  DA_Structure_img,
  Intelli_Icon,
  Our_Product_Bg,
} from "../../assets";
import { useModal } from "../../utils/demoProvider";
import { Link } from "react-router-dom";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../utils/animations";
const helmetData = new HelmetData({});

const why = [
  {
    icon: Auto_Icon,
    title: "Autonomous Insight Discovery",
    text: "Surfaces insights from your data without being asked. Detects anomalies, identifies trends, and explains what changed and why.",
  },
  {
    icon: Intelli_Icon,
    title: "Intelligent Reporting",
    text: "Generates narrative reports with reasoned analysis, not just charts. Scheduled or on demand. Distributed to the right people automatically.",
  },
  {
    icon: Control_Icon,
    title: "Controlled Actions",
    text: "Pauses campaigns, adjusts bids, triggers alerts, schedules pipelines, and more within permission scopes you configure.",
  },
  {
    icon: Convo_Query,
    title: "Conversational Querying",
    text: "Ask questions in natural language. Data Assistant translates them into queries via DataSyntax and returns reasoned answers with supporting evidence.",
  },
  {
    icon: Custom_Alert_Icon,
    title: "Custom Alerts & Monitors",
    text: "Set thresholds and conditions. Data Assistant watches continuously and acts when they're triggered.",
  },
];

export default function DataAssistantPage() {
  const { openModal } = useModal();
  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        // Hero
        maskedLineReveal(pageRef.current.querySelector(".da-hero-heading"), {
          scrollTrigger: revealTrigger(".da-hero-heading"),
        });
        riseFrom(".da-hero-copy", {
          delay: 0.2,
          scrollTrigger: revealTrigger(".da-hero-copy", "top 85%"),
        });
        riseFrom(".da-hero-cta > *", {
          delay: 0.35,
          stagger: 0.1,
          scrollTrigger: revealTrigger(".da-hero-cta", "top 90%"),
        });
        riseFrom(".da-hero-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".da-hero-media"),
        });

        // Drowning in data requests
        riseFrom(".da-problem-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".da-problem-head"),
        });
        riseFrom(".da-problem-img", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".da-problem-img"),
        });

        // The analyst that never sleeps
        riseFrom(".da-agent-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".da-agent-head"),
        });
        riseFrom(".da-agent-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".da-agent-list", "top 75%"),
        });
        riseFrom(".da-agent-img", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".da-agent-img"),
        });

        // Feature grid
        riseFrom(".da-feat-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".da-feat-head"),
        });
        riseFrom(".da-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".da-grid", "top 75%"),
        });

        // Closing CTA card
        riseFrom(".da-cta > *", {
          stagger: 0.12,
          scrollTrigger: revealTrigger(".da-cta"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>
          Data Assistant — Autonomous AI Agent for Analytics Teams
        </title>
        <link
          rel="canonical"
          href="https://analyticsintelligence.com/data-assistant"
        />
        <meta
          name="description"
          content="Data Assistant is an autonomous AI agent for analytics teams — it connects to your web, app, and marketing data, monitors continuously, surfaces insights before you ask, and takes controlled actions within your boundaries."
        />
        <meta
          name="keywords"
          content="Data Assistant, autonomous analytics agent, AI agent for digital analytics, agentic analytics, web and app analytics AI, marketing analytics agent, continuous monitoring, natural language analytics, CORE reasoning, Analytics Intelligence"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta
          property="og:title"
          content="Data Assistant — Autonomous AI Agent for Analytics Teams"
        />
        <meta
          property="og:description"
          content="An autonomous agent that connects to your web, app, and marketing data — it monitors continuously, surfaces insights before you ask, and takes controlled actions you define."
        />
        <meta
          property="og:url"
          content="https://analyticsintelligence.com/data-assistant"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta
          name="twitter:title"
          content="Data Assistant — Autonomous AI Agent for Analytics Teams"
        />
        <meta
          name="twitter:description"
          content="An autonomous agent that connects to your web, app, and marketing data — it monitors continuously, surfaces insights before you ask, and takes controlled actions you define."
        />
        <meta
          name="twitter:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Data Assistant — Autonomous Analytics Agent",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            },
            "description": "Data Assistant is an autonomous AI agent by Analytics Intelligence for digital analytics teams. It connects to web, app, and marketing data, monitors continuously, reasons with CORE, surfaces insights proactively, and takes controlled, auditable actions within user-defined boundaries.",
            "url": "https://analyticsintelligence.com/data-assistant",
            "featureList": ["Continuous monitoring", "Proactive insight discovery", "Chain-of-thought reasoning with CORE", "Controlled, auditable actions", "Conversational querying", "Custom alerts and monitors"]
          }
        `}</script>
      </Helmet>
      <main ref={pageRef}>
        {/* Hero Section */}
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16 md:gap-4 xl:gap-16  pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-4 flex flex-col items-center md:items-start">
                <span className="text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Data Assistant
                </span>
                <h2
                  className="da-hero-heading text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white"
                  dangerouslySetInnerHTML={{
                    __html:
                      "Your always-on analytics team member: it watches, reasons, and acts",
                  }}
                />
                <p className="da-hero-copy text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                  Data Assistant is an autonomous agent for digital
                  analytics teams. It connects to your web, app, and marketing
                  data, monitors continuously, surfaces insights before you ask,
                  and takes controlled actions on your behalf, within boundaries
                  you define.
                </p>
              </div>

              <div className="da-hero-cta flex gap-4">
                <a
                  href="https://dataassistant.ai/login"
                    target="_blank"
                    rel="noreferrer"
                  className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 min-w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2"
                >
                  Start Free Trial
                </a>

                <button
                  onClick={openModal}
                  className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 min-w-[170px] text-center btn-sweep btn-sweep-white text-poppingBlue rounded-[100px]"
                >
                  See a Demo
                </button>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className="da-hero-media w-full"
                src={DA_NEW_HERO}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="bg-[#010127] relative pt-[40px] pb-[96px] md:pb-[96px]">
          <div className="container relative">
            <div className="w-[150px] lg:w-[400px] h-[300px] bg-[#0073FF]/50 blur-[150px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-[80]"></div>
            <div className="flex flex-col md:flex-row-reverse items-center mt-11 gap-11 md:gap-5 z-[100] relative">
              <div class="da-problem-head flex-1 space-y-4">
                <h2 class="text-2xl text-center md:text-left md:text-[32px] font-extrabold text-white">
                  Your Team Is Drowning in Data Requests
                </h2>

                <div className="space-y-2 text-center md:text-left">
                  <h2 className="text-lg md:text-xl font-bold text-[#88BEFF]">
                    Your analytics team is the bottleneck and it's not their
                    fault.
                  </h2>
                  <p className="text-sm md:text-base text-white">
                    Every team needs answers, but your analytics team is buried
                    in requests, dashboards, and delayed insights. Dashboards
                    only show what happened; they don’t explain why or drive
                    action.
                  </p>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <img
                  src={DA_Fetaures_Img}
                  alt="Data Structures"
                  className="da-problem-img w-full h-auto md:max-w-[500px]"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#010127] relative pb-[96px]">
          <div className="container relative flex flex-col gap-11 isolate">
            <div className="w-[150px] lg:w-[400px] h-[300px] bg-[#0073FF]/50 blur-[150px] absolute top-[30%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>

            <div className="da-agent-head flex flex-col items-center text-center md:text-left z-[1000]">
              <h2 className="text-white font-extrabold text-[24px] md:text-[32px] text-center">
                Meet the Analyst That Never Sleeps
              </h2>

              <h3 className="text-lg md:text-2xl font-bold text-[#88BEFF] my-2">
                An agent that monitors, reasons, and acts.
              </h3>

              <p className="text-white text-sm md:text-base max-w-[837px] text-center">
                Data Assistant is an autonomous agent that works the way your
                best analyst would, if that analyst never slept, never had a
                backlog, and could watch every metric simultaneously.
              </p>
            </div>
            <div className="flex flex-col-reverse md:flex-row items-center mt-0 md:mt-11 gap-8">
              <div className="da-agent-list flex-1 flex flex-col gap-y-[32px] max-w-[590px]">
                <div className="da-agent-card bg-[#40406C80] backdrop-blur-[4px] p-4 rounded-xl">
                  <h4 className="text-xl font-extrabold text-white">
                    It monitors
                  </h4>
                  <p className="text-sm md:text-base text-white">
                    continuously scanning your data streams for anomalies,
                    trends, and patterns that warrant attention.
                  </p>
                </div>
                <div className="da-agent-card bg-[#40406C80] backdrop-blur-[4px] p-4 rounded-xl">
                  <h4 className="text-xl font-extrabold text-white">
                    It reasons
                  </h4>
                  <p className="text-sm md:text-base text-white">
                    using CORE's chain-of-thought reasoning to decompose
                    what's happening, diagnose why, and evaluate what to do
                    about it.
                  </p>
                </div>
                <div className="da-agent-card bg-[#40406C80] backdrop-blur-[4px] p-4 rounded-xl">
                  <h4 className="text-xl font-extrabold text-white">It acts</h4>
                  <p className="text-sm md:text-base text-white">
                    taking controlled, auditable actions within permission
                    boundaries you define. Pause an underperforming campaign.
                    Trigger an alert to the right stakeholder. Schedule a
                    report. Adjust a bid. Every action is logged, scoped, and
                    revocable.
                  </p>
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center">
                <img
                  src={DA_Structure_img}
                  alt="Data Structures"
                  className="da-agent-img md:max-w-[450px] h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#EDF1FF] py-[48px]">
          <div className="container space-y-12">
            <div className="da-feat-head">
              <h2 className="text-[#020128] font-extrabold text-[24px] md:text-[32px] text-center">
                What Intelligent Analytics Looks Like
              </h2>
            </div>

            <div className="da-grid grid grid-cols-12 gap-8">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className={`da-card group bg-white hover:bg-poppingBlue flex flex-col items-center rounded-xl px-3 py-6 gap-4 transition-colors duration-700 col-span-12 md:col-span-6 lg:col-span-4 ${
                    idx === 3 ? "lg:col-start-3" : ""
                  }`}
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16 transition-all duration-700 group-hover:brightness-0 group-hover:invert"
                  />
                  <div className="space-y-2 text-center">
                    <h5 className="text-lg md:text-xl font-bold text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="relative pt-[116px] pb-10"
          style={{
            backgroundImage: `url("${Our_Product_Bg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="container">
            <div className="da-cta bg-[#1E1E4080] backdrop-blur-[20px] flex flex-col items-center gap-6 px-4 md:px-8 py-8 sm:py-12 md:py-14 lg:py-16 xl:py-20 rounded-3xl">
              <div className="">
                <h2 className="text-[32px] md:text-[44px] text-center font-extrabold text-white max-w-[890px] leading-[1.3] md:leading-[50px]">
                  Pulse is bundled, no separate data collection cost.
                </h2>
              </div>

              <div className="flex flex-row gap-x-4 md:gap-x-8 w-full justify-center">
                <Link to="/contact">
                  <button className="text-sm md:text-base font-semibold py-3 px-4 md:px-6 sm:w-[170px] text-center btn-sweep btn-sweep-white text-poppingBlue rounded-[100px] gap-2">
                    Talk to sales
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
